import PromiseQueue from 'promise-queue';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import {
	findReferencedProductTypes,
	ReferencedProductType,
	ReferenceProductTypesResult,
} from '~/Api/SiPriceReferencing/MaxPrice';
import { retryFetchAfterUserProductGroupLock } from '~/Api/User/ProductGroup';
import {
	createExportXls,
	CreateExportXlsResult,
	ErrorResponse,
	findSiMaxPrice,
	ProductUpdate,
	SiMaxPriceResult,
	updateSiMaxPrice as apiUpdateSiMaxPrice,
	ValidationResult,
} from '~/Api/User/ProductGroup/SiMaxPrice';
import { invokeApi, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage/actions';
import { fetchGroup } from '~/ProductGroup';
import { ThunkAction } from '~/Store';

import ActionTypes from './ActionTypes';

const updateQueue = new PromiseQueue(1, Infinity);

export function fetchSiMaxPrice(groupId: string): ThunkAction<Promise<SiMaxPriceResult>, any> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findSiMaxPrice, userId, groupId));
		dispatch({ type: ActionTypes.request, groupId, request });

		return resolve(request, (response) =>
			dispatch({
				type: ActionTypes.receive,
				groupId,
				request,
				siMaxPrice: response?.siMaxPrice ?? null,
			})
		);
	};
}

export function updateSiMaxPrice(
	groupId: string,
	referenceBasketVersion: string | null,
	referencedProductType: string | null,
	packageSize: number | null,
	dosageStrength: number | null,
	historicProductDataViewDate: Date | null,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, any> {
	return (dispatch) => {
		const request = updateQueue.add(() =>
			dispatch(
				immediatelyUpdateSiMaxPrice(
					groupId,
					referenceBasketVersion,
					referencedProductType,
					packageSize,
					dosageStrength,
					historicProductDataViewDate,
					products
				)
			)
		);
		dispatch({ type: ActionTypes.requestUpdate, groupId, request });
		return request;
	};
}

function immediatelyUpdateSiMaxPrice(
	groupId: string,
	referenceBasketVersion: string | null,
	referencedProductType: string | null,
	packageSize: number | null,
	dosageStrength: number | null,
	historicProductDataViewDate: Date | null,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, any> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(
				invokeApi(
					apiUpdateSiMaxPrice,
					userId,
					groupId,
					referenceBasketVersion,
					referencedProductType,
					packageSize,
					dosageStrength,
					historicProductDataViewDate,
					products
				)
			)
		);
		dispatch({ type: ActionTypes.requestUpdate, groupId, request });

		await resolve(request, (response) =>
			dispatch({
				type: ActionTypes.receiveUpdate,
				groupId,
				request,
				siMaxPrice: response?.siMaxPrice || null,
			})
		);
	};
}

export function handleSiMaxPriceUpdateResponse(response: any, groupId: string): ThunkAction<void, any> {
	return (dispatch) => {
		if (!(response instanceof ErrorResponse)) {
			dispatch(handleError(response));
			return;
		}

		if (response.handleProductGroupNotFound()) {
			dispatch(fetchGroup(groupId));
		}
		if (response.handleCannotChangeSettingsForNonMemberProducts()) {
			dispatch(fetchSiMaxPrice(groupId)).catch((response) => handleError(response));
		}
		if (response.handleIllegalPriceTypeSelected()) {
			dispatch(flashErrorMessage('Product reference prices has changed, please check selected product prices'));
			dispatch(fetchSiMaxPrice(groupId)).catch((response) => handleError(response));
		}

		dispatch(handleError(response));
	};
}

export function createSiMaxPriceExportXls(groupId: string): ThunkAction<Promise<CreateExportXlsResult>, any> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(createExportXls, userId, groupId));
		dispatch({ type: ActionTypes.requestXlsCreate, groupId, request });

		return resolve(request, (response, error) =>
			dispatch({
				type: ActionTypes.receiveXlsCreate,
				request,
				validationResult: error
					? error instanceof ErrorResponse
						? error.toValidationResult()
						: null
					: new ValidationResult(false, [], []),
			})
		);
	};
}

export function fetchReferenceProductTypes(): ThunkAction<Promise<ReferenceProductTypesResult>, any> {
	return async (dispatch, getState) => {
		const { productTypes } = getState().siMaxPrice;
		if (productTypes !== null) {
			return productTypes.request;
		}

		const request = dispatch(invokeApi(findReferencedProductTypes));
		dispatch({ type: ActionTypes.requestProductTypes, request });

		return resolve(request, (response) =>
			dispatch({
				type: ActionTypes.receiveProductTypes,
				request,
				types: response?.items || null,
			})
		);
	};
}

export function pruneSiMaxPrice(groupId: string): { type: string; groupId: string } {
	return { type: ActionTypes.prune, groupId };
}

export function scheduleSiMaxPriceUpdate(
	groupId: string,
	referenceBasketVersion: string,
	referencedProductType: string | null,
	packageSize: number | null,
	dosageStrength: number | null,
	historicProductDataViewDate: Date | null,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, any> {
	return (dispatch) => {
		return new Promise((resolve) => {
			const timeoutId = setTimeout(() => {
				resolve(
					dispatch(
						updateSiMaxPrice(
							groupId,
							referenceBasketVersion,
							referencedProductType,
							packageSize,
							dosageStrength,
							historicProductDataViewDate,
							products
						)
					)
				);
			}, 2000);

			dispatch({
				type: ActionTypes.scheduleUpdate,
				timeoutId,
				groupId,
				referenceBasketVersion,
				referencedProductType,
				packageSize,
				dosageStrength,
				historicProductDataViewDate,
				products,
			});
		});
	};
}

export function commitSiMaxPriceUpdate(groupId: string): ThunkAction<Promise<void>, any> {
	return async (dispatch, getState) => {
		const { update } = getState().siMaxPrice;
		if (!update || update?.groupId !== groupId) {
			return;
		}
		if (update.state.name === 'fetching') {
			await update.state.request;
		}
		if (update.state.name === 'scheduled') {
			const {
				timeoutId,
				referenceBasketVersion,
				referencedProductType,
				packageSize,
				dosageStrength,
				historicProductDataViewDate,
				products,
			} = update.state;
			clearTimeout(timeoutId);
			await dispatch(
				updateSiMaxPrice(
					groupId,
					referenceBasketVersion,
					referencedProductType,
					packageSize,
					dosageStrength,
					historicProductDataViewDate,
					products
				)
			);
		}
	};
}

export function handleCommitError(error: any): ThunkAction<void, any> {
	return (dispatch) => {
		if (!(error instanceof ErrorResponse)) {
			dispatch(handleError(error));
			return;
		}

		error.handleProductGroupNotFound();
		error.handleCannotChangeSettingsForNonMemberProducts();
		error.handleIllegalPriceTypeSelected();
		dispatch(handleError(error));
	};
}
